import { useMemo } from 'react'
import type { JSX } from 'react'

import { useSplitEnabled } from '@buffer-mono/features'
import { Flex } from '@buffer-mono/popcorn'

import type { ContentItemCard_ContentItemData } from '~publish/components/ContentItemCard'
import { usePaginatedContentItems } from '~publish/components/ContentItemCard'
import { UNTAGGED_VALUE } from '~publish/components/FilterByTag'
import type { MaskedFilterByChannelFragment } from '~publish/components/FilterByChannel'
import {
  splitNoChannelsSelection,
  useFilterByChannelFragment,
} from '~publish/components/FilterByChannel'
import { useTagsQuery } from '~publish/hooks/useTags'

import { BoardColumnContainer } from './BoardColumnContainer'
import type { BoardColumnConfig } from './BoardColumnConfig'
import type { PostStatus } from '../PostList/usePaginatedPostList'
import type { BoardState } from './useBoardState'

import styles from './BoardColumns.module.css'

const STATUS_COLUMNS: {
  key: string
  label: string
  statuses: PostStatus[]
  windowRule: BoardColumnConfig['windowRule']
}[] = [
  { key: 'draft', label: 'Draft', statuses: ['draft'], windowRule: 'none' },
  {
    key: 'needs_approval',
    label: 'Approvals',
    statuses: ['needs_approval'],
    windowRule: 'none',
  },
  {
    key: 'scheduled',
    label: 'Scheduled',
    statuses: ['scheduled', 'sending'],
    windowRule: 'upcoming-30-days',
  },
  {
    key: 'sent',
    label: 'Published',
    statuses: ['sent'],
    windowRule: 'past-30-days',
  },
  { key: 'error', label: 'Failed', statuses: ['error'], windowRule: 'none' },
]

/**
 * Every filter is always active now (Channels/Tags/Status/Date all stay
 * visible regardless of grouping mode), so content items — which are always
 * conceptually "draft" and never carry a channel — respect Status and Tags
 * uniformly across all three grouping modes. Channels never narrows them:
 * channel-less is its own dimension, not something a channel filter selects.
 */
function getVisibleContentItems(
  items: ContentItemCard_ContentItemData[],
  { statuses, tagIds }: { statuses: PostStatus[]; tagIds: string[] },
): ContentItemCard_ContentItemData[] {
  if (statuses.length > 0 && !statuses.includes('draft')) {
    return []
  }
  if (tagIds.length > 0) {
    const wantsUntagged = tagIds.includes(UNTAGGED_VALUE)
    return items.filter(
      (item) =>
        (wantsUntagged && item.tags.length === 0) ||
        item.tags.some((tag) => tagIds.includes(tag.id)),
    )
  }
  return items
}

type BoardColumnsProps = {
  state: BoardState
  channels: MaskedFilterByChannelFragment
}

export function BoardColumns({
  state,
  channels: maskedChannels,
}: BoardColumnsProps): JSX.Element {
  const channels = useFilterByChannelFragment(maskedChannels)
  const { tags } = useTagsQuery()
  const {
    isEnabled: isContentsQueryEnabled,
    isReady: isContentsQueryFlagReady,
  } = useSplitEnabled('board-view-contents-query')
  const {
    draftContentItems,
    hasNextPage: contentItemsHasNextPage,
    lastElementRef: contentItemsLastElementRef,
  } = usePaginatedContentItems()
  const visibleContentItems = useMemo(
    () =>
      getVisibleContentItems(draftContentItems, {
        statuses: state.statuses,
        tagIds: state.tagIds,
      }),
    [draftContentItems, state.statuses, state.tagIds],
  )
  // Content items are one shared paginated list bucketed across columns —
  // the "load more" trigger belongs on whichever column actually renders the
  // last VISIBLE item, not the connection's raw last item: a Status/Tags
  // filter can exclude that one from every rendered column, which would
  // otherwise leave no column wired to fetch more at all.
  const lastContentItemId = visibleContentItems.at(-1)?.id

  const columns = useMemo((): BoardColumnConfig[] => {
    const { isNoChannelsSelected, realChannelIds } = splitNoChannelsSelection(
      state.channelIds,
    )
    const activeChannelIds = realChannelIds.length ? realChannelIds : undefined
    const activeTagIds = state.tagIds.length ? state.tagIds : undefined

    if (state.groupBy === 'tag') {
      // The flag picks which query each column runs. Waiting for it keeps the
      // columns from fetching through one query and then refetching through
      // the other.
      if (!isContentsQueryFlagReady) return []
      // Every filter is always active, including the one matching the
      // grouping dimension — a Tags selection narrows which tag columns
      // render, not just which posts each column's query returns.
      const visibleTags = activeTagIds
        ? tags.filter((tag) => activeTagIds.includes(tag.id))
        : tags
      // With the flag on, each Tag column reads posts and content items from
      // one `contents` query. Otherwise it merges `posts` with its share of
      // the shared content-items connection.
      const tagColumns: BoardColumnConfig[] = visibleTags.map((tag) => ({
        key: tag.id,
        label: tag.name,
        statuses: state.statuses,
        channelIds: activeChannelIds,
        tagIds: [tag.id],
        windowRule: 'none',
        tagColor: tag.color,
        contentItems: isContentsQueryEnabled
          ? []
          : visibleContentItems.filter((item) =>
              item.tags.some((itemTag) => itemTag.id === tag.id),
            ),
        useUnifiedContentsQuery: isContentsQueryEnabled,
      }))
      const untaggedColumn: BoardColumnConfig = {
        key: 'untagged',
        label: 'Untagged',
        statuses: state.statuses,
        channelIds: activeChannelIds,
        tagIds: [UNTAGGED_VALUE],
        windowRule: 'none',
        contentItems: isContentsQueryEnabled
          ? []
          : visibleContentItems.filter((item) => item.tags.length === 0),
        useUnifiedContentsQuery: isContentsQueryEnabled,
      }
      return isContentsQueryEnabled
        ? [untaggedColumn, ...tagColumns]
        : [...tagColumns, untaggedColumn]
    }

    if (state.groupBy === 'channel') {
      // Same idea for Channels: a selection narrows which channel columns
      // render. "No channel" is a distinct pinned bucket, not a real
      // channel, so it stays regardless of the selection.
      const visibleChannels = activeChannelIds
        ? channels.filter((channel) => activeChannelIds.includes(channel.id))
        : channels
      const noChannelColumn: BoardColumnConfig = {
        key: 'no-channel',
        label: 'No channel',
        statuses: [],
        postsDisabled: true,
        windowRule: 'none',
        contentItems: visibleContentItems,
      }
      const channelColumns: BoardColumnConfig[] = visibleChannels.map(
        (channel) => ({
          key: channel.id,
          label: channel.name,
          statuses: state.statuses,
          channelIds: [channel.id],
          tagIds: activeTagIds,
          windowRule: 'none',
          channelAvatar: { service: channel.service, src: channel.avatar },
          contentItems: [],
        }),
      )
      return [noChannelColumn, ...channelColumns]
    }

    // Status grouping (default): a Status selection narrows which status
    // columns render — kept if any of its statuses are in the selection.
    const visibleStatusColumns = state.statuses.length
      ? STATUS_COLUMNS.filter((column) =>
          column.statuses.some((status) => state.statuses.includes(status)),
        )
      : STATUS_COLUMNS
    return visibleStatusColumns.map((column) => ({
      key: column.key,
      label: column.label,
      statuses: column.statuses,
      channelIds: activeChannelIds,
      tagIds: activeTagIds,
      windowRule: column.windowRule,
      contentItems: [],
      useUnifiedDraftsQuery: column.key === 'draft',
      includeChannellessDrafts: column.key === 'draft' && isNoChannelsSelected,
    }))
  }, [
    state.groupBy,
    state.statuses,
    state.channelIds,
    state.tagIds,
    tags,
    channels,
    visibleContentItems,
    isContentsQueryEnabled,
    isContentsQueryFlagReady,
  ])

  return (
    <Flex gap="md" className={styles.columns}>
      {columns.map((column) => (
        <BoardColumnContainer
          key={column.key}
          config={column}
          dateRange={state.dateRange}
          contentItemsHasNextPage={contentItemsHasNextPage}
          contentItemsLastElementRef={contentItemsLastElementRef}
          lastContentItemId={lastContentItemId}
        />
      ))}
    </Flex>
  )
}
